import { type FC, useEffect } from 'react';

import { omit, pick } from 'lodash-es';

import { selectIsNltfActive } from '../../ducks/nltf/selectors';
import {
  getFacets,
  reloadSearchItems,
  updateUrlArgs,
} from '../../ducks/pages/search/thunks';
import { searchFilterActions } from '../../ducks/panels/search_filters/actions';
import { SEARCH_QUERY_FILTER_KEY } from '../../ducks/search/active_filter_chips';
import { getIsNltfAvailable } from '../../ducks/settings/select_features';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import { selectUserRoles } from '../../utils/get_user_roles';
import SearchFiltersPanel from '../search_filters/search_filters';

const GridSearchFiltersPanel: FC = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getFacets());
  }, []);

  const userRoles = useAppSelector(selectUserRoles);
  const dateFormat = useAppSelector((state) => state.appReducer.dateFormat);
  const { searchFilters } = useAppSelector((state) => state.panelsState);
  const { availableFacets, filterValues, facetValues, query } = searchFilters;
  const disabledFilters = searchFilters.disabledFilters ?? [];

  const isNltfEnabled = useAppSelector(getIsNltfAvailable);
  const isNltfActive = useAppSelector(selectIsNltfActive);
  const showLeftoverQuery = isNltfEnabled && isNltfActive;
  const isQueryDisabled = disabledFilters.includes(SEARCH_QUERY_FILTER_KEY);
  let leftoverQuery: string | undefined;
  if (showLeftoverQuery) {
    leftoverQuery = isQueryDisabled ? '' : query;
  }

  return (
    <SearchFiltersPanel
      onFilterSearch={(data) => {
        const formFilters = data.filters ?? {};
        const formFacets = data.facets_filters ?? {};
        // Disabled filters are hidden from the form, so a key still absent from
        // it stays disabled and keeps its stashed value; a key the user set is
        // dropped from disabledFilters and becomes active again.
        const stillDisabledFilterKeys = disabledFilters.filter(
          (key) => key in filterValues && !(key in formFilters),
        );
        const stillDisabledFacetKeys = disabledFilters.filter(
          (key) => key in facetValues && !(key in formFacets),
        );
        // Keys with no filter/facet value — the query sentinel — aren't part of
        // the form at all, so a filter edit must leave them alone.
        const stillDisabledOtherKeys = disabledFilters.filter(
          (key) => !(key in filterValues) && !(key in facetValues),
        );
        const nextDisabled = [
          ...stillDisabledFilterKeys,
          ...stillDisabledFacetKeys,
          ...stillDisabledOtherKeys,
        ];
        dispatch(
          searchFilterActions.setFilterValues({
            ...pick(filterValues, stillDisabledFilterKeys),
            ...formFilters,
          }),
        );
        dispatch(
          searchFilterActions.setFacetValues({
            ...pick(facetValues, stillDisabledFacetKeys),
            ...formFacets,
          }),
        );
        if (nextDisabled.length !== disabledFilters.length) {
          dispatch(searchFilterActions.setDisabledFilters(nextDisabled));
        }
        dispatch(updateUrlArgs({ page: 1 }));
        dispatch(reloadSearchItems(true));
      }}
      // A disabled query hides its value here, like the other disabled filters;
      // the value itself stays in Redux and on the dimmed chip.
      searchQuery={leftoverQuery}
      onSearchString={
        showLeftoverQuery
          ? (value, event) => {
              // No event means the programmatic sync from a prop change, not a
              // user edit — bail to avoid triggering a plain search that would
              // destroy NLS results.
              if (!event) {
                return;
              }
              dispatch(searchFilterActions.setQuery(value ?? ''));
              // A real edit re-enables the query.
              dispatch(
                searchFilterActions.setFilterDisabled({
                  name: SEARCH_QUERY_FILTER_KEY,
                  disabled: false,
                }),
              );
              dispatch(updateUrlArgs({ search: value ?? '', page: 1 }));
              dispatch(reloadSearchItems(true));
            }
          : undefined
      }
      dateFormat={dateFormat}
      filterValues={omit(filterValues, disabledFilters)}
      facetValues={omit(facetValues, disabledFilters)}
      canReadMetadataViews={userRoles.canReadMetadataViews}
      availableFacets={availableFacets}
    />
  );
};

export default GridSearchFiltersPanel;
